/**
 * SeasonalityRowComponents
 *
 * Reusable row components for the SeasonalityModeView table.
 * Extracted to reduce the main component's size and improve maintainability.
 */

import React from 'react';
import { MonthlyTotals, MONTH_NAMES, getMonthValue } from '../../types';
import { getVarianceColor } from './seasonalityModeHelpers';

/**
 * Common props shared by row components
 */
interface RowCommonProps {
  comparisonMode?: boolean;
  visibleActualMonths: Set<number>;
  formatCurrency: (value: number) => string;
}

/**
 * Props for MonthlyDataRow component
 */
interface MonthlyDataRowProps extends RowCommonProps {
  label: string;
  monthly: MonthlyTotals;
  annualTotal: number;
  colorClass: string;
  isBold?: boolean;
  indent?: number;
  actualMonthly?: MonthlyTotals | null;
  actualAnnual?: number | null;
  isExpense?: boolean;
  /** Rendered after the label (e.g. the Executive adjust button / adjusted badge) */
  labelAdornment?: React.ReactNode;
}

/**
 * Renders a row with monthly currency data and optional actual comparison columns
 */
export const MonthlyDataRow: React.FC<MonthlyDataRowProps> = ({
  label,
  monthly,
  annualTotal,
  colorClass,
  isBold = false,
  indent = 0,
  actualMonthly,
  actualAnnual,
  isExpense = false,
  comparisonMode = false,
  visibleActualMonths,
  formatCurrency,
  labelAdornment,
}) => {
  const hasActualData = comparisonMode && actualMonthly !== undefined && actualMonthly !== null;
  const actualYtd = actualAnnual ?? 0;

  return (
    <tr className={`${colorClass} border-b border-gray-200`}>
      <td
        className={`min-w-[180px] px-3 py-2 text-sm ${isBold ? 'font-bold' : 'font-medium'} sticky left-0 ${colorClass}`}
        style={{ paddingLeft: `${12 + indent * 16}px` }}
      >
        {labelAdornment ? <span className="flex items-center gap-1">{label}{labelAdornment}</span> : label}
      </td>
      {MONTH_NAMES.map((_, i) => {
        const monthNum = i + 1;
        const projectedValue = getMonthValue(monthly, monthNum);
        const showActual = comparisonMode && visibleActualMonths.has(monthNum);
        const actualValue = hasActualData ? getMonthValue(actualMonthly!, monthNum) : 0;
        const varianceColor = hasActualData ? getVarianceColor(actualValue, projectedValue, isExpense) : '';

        return (
          <React.Fragment key={i}>
            {/* Projected column */}
            <td className={`min-w-[70px] px-1 py-2 text-right text-xs ${isBold ? 'font-semibold' : ''}`}>
              {formatCurrency(projectedValue)}
            </td>
            {/* Actual column (only shown when month is toggled) */}
            {showActual && (
              <td className={`min-w-[70px] px-1 py-2 text-right text-xs bg-blue-50/50 ${isBold ? 'font-semibold' : ''} ${varianceColor}`}>
                {hasActualData && actualValue !== 0 ? formatCurrency(actualValue) : <span className="text-gray-300">--</span>}
              </td>
            )}
          </React.Fragment>
        );
      })}
      <td className={`min-w-[100px] px-2 py-2 text-right text-sm font-bold bg-opacity-50 ${colorClass}`}>
        {formatCurrency(annualTotal)}
      </td>
      {/* Annual Actual column when any month is showing actuals */}
      {comparisonMode && visibleActualMonths.size > 0 && (
        <td className={`min-w-[100px] px-2 py-2 text-right text-sm font-bold bg-blue-50/50 ${hasActualData ? getVarianceColor(actualYtd, annualTotal, isExpense) : ''}`}>
          {hasActualData && actualYtd !== 0 ? formatCurrency(actualYtd) : <span className="text-gray-300">--</span>}
        </td>
      )}
    </tr>
  );
};

/**
 * Props for UnitDataRow component
 */
interface UnitDataRowProps extends RowCommonProps {
  label: string;
  monthly: MonthlyTotals;
  annualTotal: number;
  colorClass: string;
  indent?: number;
  actualMonthly?: MonthlyTotals | null;
  actualAnnual?: number | null;
}

/**
 * Renders a row with monthly unit count data (integers, not currency)
 */
export const UnitDataRow: React.FC<UnitDataRowProps> = ({
  label,
  monthly,
  annualTotal,
  colorClass,
  indent = 0,
  actualMonthly,
  actualAnnual,
  comparisonMode = false,
  visibleActualMonths,
}) => {
  const hasActualData = comparisonMode && actualMonthly !== undefined && actualMonthly !== null;
  const actualYtd = actualAnnual ?? 0;

  return (
    <tr className={`${colorClass} border-b border-gray-100`}>
      <td
        className={`min-w-[180px] px-3 py-1.5 text-xs sticky left-0 ${colorClass}`}
        style={{ paddingLeft: `${12 + indent * 16}px` }}
      >
        {label}
      </td>
      {MONTH_NAMES.map((_, i) => {
        const monthNum = i + 1;
        const showActual = comparisonMode && visibleActualMonths.has(monthNum);
        const projValue = getMonthValue(monthly, monthNum);
        const actualValue = hasActualData ? getMonthValue(actualMonthly!, monthNum) : 0;
        const varianceColor = hasActualData ? getVarianceColor(actualValue, projValue, false) : '';

        return (
          <React.Fragment key={i}>
            <td className="min-w-[70px] px-1 py-1.5 text-right text-xs">
              {projValue.toLocaleString()}
            </td>
            {showActual && (
              <td className={`min-w-[70px] px-1 py-1.5 text-right text-xs bg-blue-50/50 ${varianceColor}`}>
                {hasActualData && actualValue !== 0 ? actualValue.toLocaleString() : <span className="text-gray-300">--</span>}
              </td>
            )}
          </React.Fragment>
        );
      })}
      <td className={`min-w-[100px] px-2 py-1.5 text-right text-xs font-semibold ${colorClass}`}>
        {annualTotal.toLocaleString()}
      </td>
      {comparisonMode && visibleActualMonths.size > 0 && (
        <td className={`min-w-[100px] px-2 py-1.5 text-right text-xs font-semibold bg-blue-50/50 ${hasActualData ? getVarianceColor(actualYtd, annualTotal, false) : ''}`}>
          {hasActualData && actualYtd !== 0 ? actualYtd.toLocaleString() : <span className="text-gray-300">--</span>}
        </td>
      )}
    </tr>
  );
};
